import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import MenuItem from "@mui/material/MenuItem"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"

export const TRIAL_STATUS_OPTIONS = [
  { value: "live", label: "Live" },
  { value: "trial", label: "Trial" },
  { value: "dunning", label: "Dunning" },
  { value: "unpaid", label: "Unpaid" },
  { value: "non_renewing", label: "Non renewing" },
  { value: "cancelled", label: "Cancelled" },
  { value: "creation_failed", label: "Creation failed" },
  { value: "cancelled_from_dunning", label: "Cancelled from dunning" },
  { value: "expired", label: "Expired" },
  { value: "trial_expired", label: "Trial expired" },
  { value: "future", label: "Future" },
]

function toDateInputValue(value) {
  if (!value) return ""
  return String(value).slice(0, 10)
}

export default function AdminTrialEditDialog({
  open,
  trial,
  onClose,
  onSave,
  saving,
}) {
  const [form, setForm] = useState({
    current_term_ends_at: "",
    expires_at: "",
    free_candidates_remaining: 50,
    status: "trial",
  })

  useEffect(() => {
    if (!trial) return
    setForm({
      current_term_ends_at: toDateInputValue(trial.current_term_ends_at),
      expires_at: toDateInputValue(trial.expires_at),
      free_candidates_remaining: trial.free_candidates_remaining ?? 50,
      status: trial.status || "trial",
    })
  }, [trial])

  function handleSubmit(event) {
    event.preventDefault()
    onSave({
      current_term_ends_at: form.current_term_ends_at || null,
      expires_at: form.expires_at || null,
      free_candidates_remaining: Number(form.free_candidates_remaining),
      status: form.status,
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Edit trial</DialogTitle>
        <DialogContent>
          <Column gap={2} sx={{ pt: 1 }}>
            <TextField
              label="Email"
              value={trial?.email || "—"}
              size="small"
              fullWidth
              disabled
            />
            <TextField
              label="Trial ends on"
              type="date"
              value={form.current_term_ends_at}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  current_term_ends_at: e.target.value,
                }))
              }
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Valid until"
              type="date"
              value={form.expires_at}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, expires_at: e.target.value }))
              }
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Free candidates remaining"
              type="number"
              value={form.free_candidates_remaining}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  free_candidates_remaining: e.target.value,
                }))
              }
              size="small"
              fullWidth
              inputProps={{ min: 0, max: 50 }}
            />
            <TextField
              select
              label="Subscription status"
              value={form.status}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, status: e.target.value }))
              }
              size="small"
              fullWidth
            >
              {TRIAL_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="text.secondary">
              Same editable fields as Django admin Trial Management — no create
              or delete actions.
            </Typography>
          </Column>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

AdminTrialEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  trial: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
}
